/* The Aesthetic Cove — the Christmas landing (snippets/christmas-landing.liquid).

   1. The bauble. A laser writes sample names onto the clear acrylic bauble in the
      hero, one after another, until the visitor types a name of their own. Then it
      engraves theirs, and re-engraves as they change it.
   2. The name tags. The visitor's name hangs on a paper tag ("For Olivia") on every
      gift on the page that takes a name, and shows in the gift bar. It is kept for
      the visit in sessionStorage so it is still there after a product page and Back.
      Nothing is sent anywhere.
   3. The gift bar. Price chips filter every row (searching each whole category, not
      only the first few shown); rows with nothing in range fold away and their tile
      dims. "All gifts" puts the page back as it was.

   Visitors who prefer reduced motion get the name straight away, without the laser. */
(() => {
  const root = document.querySelector('[data-xl]');
  if (!root || root.dataset.ready) return;
  root.dataset.ready = '1';

  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const KEY = 'tac-gift-name';
  const store = {
    get() {
      try { return sessionStorage.getItem(KEY) || ''; } catch (e) { return ''; }
    },
    set(v) {
      try { v ? sessionStorage.setItem(KEY, v) : sessionStorage.removeItem(KEY); } catch (e) { /* private mode */ }
    },
  };

  // "olivia rose" -> "Olivia Rose"; letters, spaces, &, hyphens and apostrophes only.
  const tidy = (raw) =>
    (raw || '')
      .replace(/[^\p{L}\p{M}' ’.&-]/gu, '')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 18)
      .split(' ')
      .map((w) => w.charAt(0).toLocaleUpperCase() + w.slice(1))
      .join(' ');

  /* ---------- 1. the bauble ---------- */
  const bauble = root.querySelector('[data-xl-bauble]');
  const ink = bauble && bauble.querySelector('[data-xl-ink]');
  // The writing progress lives on the name itself, so each frame of the laser
  // restyles a line of text and not the whole glass bauble around it.
  const nameLine = ink && ink.parentElement;
  const samples = bauble ? (bauble.dataset.names || '').split(',').map((s) => s.trim()).filter(Boolean) : [];
  let raf = 0;

  const write = (name, animate) => {
    if (!ink) return;
    cancelAnimationFrame(raf);
    ink.textContent = name;
    nameLine.style.setProperty('--len', Math.max(4, name.length));
    if (!animate || reduce) {
      nameLine.style.setProperty('--p', 1);
      bauble.classList.remove('is-writing');
      return;
    }
    const duration = 500 + name.length * 75;
    const start = performance.now();
    nameLine.style.setProperty('--p', 0);
    bauble.classList.add('is-writing');
    const step = (now) => {
      const p = Math.min(1, (now - start) / duration);
      nameLine.style.setProperty('--p', p.toFixed(3));
      if (p < 1) raf = requestAnimationFrame(step);
      else bauble.classList.remove('is-writing');
    };
    raf = requestAnimationFrame(step);
  };

  // The sample names, while nobody has typed one and the hero is on screen.
  let userName = '';
  let demoIndex = 1;
  let demoTimer = 0;
  let heroVisible = true;
  const stopDemo = () => clearTimeout(demoTimer);
  const runDemo = () => {
    stopDemo();
    if (userName || reduce || samples.length < 2 || !heroVisible || document.hidden) return;
    demoTimer = setTimeout(() => {
      write(samples[demoIndex++ % samples.length], true);
      runDemo();
    }, 3200);
  };
  if (bauble && 'IntersectionObserver' in window) {
    new IntersectionObserver(([entry]) => {
      heroVisible = entry.isIntersecting;
      heroVisible ? runDemo() : stopDemo();
    }).observe(bauble);
  }
  document.addEventListener('visibilitychange', () => (document.hidden ? stopDemo() : runDemo()));

  /* ---------- 2. the name, everywhere ---------- */
  const form = root.querySelector('[data-xl-form]');
  const input = root.querySelector('[data-xl-input]');
  const who = root.querySelector('[data-xl-who]');

  const buy = root.querySelector('[data-xl-buy]');
  const apply = (name) => {
    userName = name;
    root.classList.toggle('has-name', Boolean(name));
    if (buy) buy.href = buy.dataset.href + (name ? `?name=${encodeURIComponent(name)}` : '');
    root.querySelectorAll('[data-xl-name]').forEach((el) => (el.textContent = name));
    if (who) who.hidden = !name;
    store.set(name);
  };

  let typing = 0;
  if (input) {
    input.addEventListener('input', () => {
      clearTimeout(typing);
      typing = setTimeout(() => {
        const name = tidy(input.value);
        if (name === userName) return;
        if (name) {
          stopDemo();
          write(name, true);
          apply(name);
        } else {
          apply('');
          runDemo();
        }
      }, 380);
    });
  }

  const gifts = root.querySelector('#xl-gifts') || root.querySelector('.xl-row');
  if (form) {
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      clearTimeout(typing);
      const name = tidy(input && input.value);
      if (name && name !== userName) {
        stopDemo();
        write(name, true);
        apply(name);
      }
      if (input) input.blur();
      if (gifts) gifts.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    });
  }

  const change = root.querySelector('[data-xl-change]');
  if (change && input) {
    change.addEventListener('click', () => {
      input.closest('section').scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
      setTimeout(() => { input.focus({ preventScroll: true }); input.select(); }, reduce ? 0 : 500);
    });
  }

  const saved = tidy(store.get());
  if (saved) {
    if (input) input.value = saved;
    write(saved, false);
    apply(saved);
  } else {
    runDemo();
  }

  /* ---------- 3. the gift bar ---------- */
  // The bar sticks under the header, so it needs the header's real height.
  const header = document.querySelector('sticky-header');
  if (header) {
    const measure = () => root.style.setProperty('--xl-header', `${Math.round(header.getBoundingClientRect().height)}px`);
    measure();
    if ('ResizeObserver' in window) new ResizeObserver(measure).observe(header);
  }

  const chips = [...root.querySelectorAll('[data-xl-price]')];
  const items = [...root.querySelectorAll('.xl-item')];
  const rows = [...root.querySelectorAll('.xl-row[id^="xmas-"]')];
  const count = root.querySelector('[data-xl-count]');
  const bar = root.querySelector('[data-xl-bar]');

  chips.forEach((chip) => {
    chip.addEventListener('click', () => {
      chips.forEach((c) => c.setAttribute('aria-pressed', String(c === chip)));
      const range = chip.dataset.xlPrice;
      const filtering = Boolean(range);
      const [lo, hi] = filtering ? range.split('-') : [];
      const min = Number(lo) || 0;
      const max = hi === '' || hi === undefined ? Infinity : Number(hi);
      let shown = 0;

      items.forEach((item) => {
        const price = Number(item.dataset.price);
        const show = filtering ? price >= min && price <= max : !('extra' in item.dataset);
        item.hidden = !show;
        if (show) shown++;
      });
      rows.forEach((row) => {
        const any = row.querySelector('.xl-item:not([hidden])');
        row.hidden = !any;
        const more = row.querySelector('.xl-row__more');
        if (more) more.hidden = filtering;
        const tile = root.querySelector(`.xc__tile[href="#${row.id}"]`);
        if (tile) tile.classList.toggle('is-empty', !any);
      });
      if (count) count.textContent = filtering ? `${shown} gifts, ${chip.textContent.trim()}` : '';

      // If the reader has scrolled past the bar, bring them back to the top of the
      // filtered list with the bar sitting just under the header (scrollIntoView put
      // the bar at the very top of the screen, behind the header).
      if (bar) {
        const before = bar.previousElementSibling;
        const stickyTop = parseFloat(getComputedStyle(bar).top) || 0;
        const natural = before ? before.getBoundingClientRect().bottom + window.scrollY : bar.getBoundingClientRect().top + window.scrollY;
        const target = Math.max(0, Math.round(natural - stickyTop));
        if (window.scrollY > target) window.scrollTo({ top: target, behavior: 'auto' });
      }
    });
  });
})();
